import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Star, Share2, MapPin, Home } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const JobCard = ({ job, saved = false, onToggleSaved }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSaved, setIsSaved] = useState(saved);
  const [busy, setBusy] = useState(false);

  const money = (v) => (v == null ? "" : `$${Number(v).toFixed(0)}`);
  const salary =
    job.salary_min != null && job.salary_max != null
      ? `${money(job.salary_min)} - ${money(job.salary_max)} ${t("card.perHour")}`
      : job.salary_min != null
      ? `${money(job.salary_min)} ${t("card.perHour")}`
      : "";

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!user || user.role !== "candidate") {
      toast.error(t("detail.loginToApply"));
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      if (isSaved) {
        await api.delete(`/jobs/${job.id}/save`);
      } else {
        await api.post(`/jobs/${job.id}/save`);
      }
      const next = !isSaved;
      setIsSaved(next);
      onToggleSaved?.(job.id, next);
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/jobs/${job.id}`;
    try {
      api.post(`/jobs/${job.id}/share`).catch(() => {});
      if (navigator.share) {
        await navigator.share({ title: job.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t("detail.shareCopied"));
      }
    } catch (err) { /* user cancelled */ }
  };

  return (
    <div
      data-testid="job-card-item"
      onClick={() => navigate(`/jobs/${job.id}`)}
      className="bg-white rounded-2xl border border-stone-border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer overflow-hidden group"
    >
      {job.offers_housing && (
        <div className="bg-housing text-white text-xs font-bold tracking-wide uppercase px-4 py-1.5 flex items-center gap-1.5">
          <Home className="w-3.5 h-3.5" /> {t("card.housing")}
        </div>
      )}
      <div className="p-4 sm:p-5 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-semibold text-stone-text leading-tight truncate group-hover:text-brand transition-colors">
              {job.title}
            </h3>
            <p className="text-sm text-stone-sub truncate">
              {job.restaurant?.name || ""}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              data-testid="job-card-save-btn"
              onClick={handleSave}
              disabled={busy}
              className="p-2 rounded-full hover:bg-brand-soft transition-colors"
              aria-label={t("card.save")}
            >
              <Star className={`w-5 h-5 ${isSaved ? "fill-brand text-brand" : "text-stone-muted"}`} />
            </button>
            <button
              data-testid="job-card-share-btn"
              onClick={handleShare}
              className="p-2 rounded-full hover:bg-brand-soft transition-colors"
              aria-label={t("card.share")}
            >
              <Share2 className="w-5 h-5 text-stone-muted" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-1 text-sm text-stone-muted">
          <MapPin className="w-4 h-4" /> {job.city}{job.state ? `, ${job.state}` : ""}
        </div>

        {salary && (
          <div className="text-brand font-heading font-bold text-lg">{salary}</div>
        )}

        <div className="flex flex-wrap gap-2 mt-1">
          {!job.requires_experience && (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-stone-bg text-stone-sub border border-stone-border">
              {t("card.noExp")}
            </span>
          )}
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-stone-bg text-stone-sub border border-stone-border">
            {job.contract_type === "part_time" ? t("card.partTime") : t("card.fullTime")}
          </span>
          {job.position_type && (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-brand-soft text-brand">
              {job.position_type}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
